import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, Sparkles, Printer, AlertTriangle, CheckCircle2, 
  Pencil, Check, X, Calendar, User, PlusCircle, RefreshCw
} from 'lucide-react';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { useSession } from '@/lib/SessionContext';
import { toast } from 'sonner';
import { 
  fetchBatchMasters, 
  generateBatchCode, 
  findBatchVariant, 
  registerNewVariantToBatchMaster, 
  appendBatchHistory 
} from '@/lib/batchHelper';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';

const SpkPrintModal = ({ 
  isOpen, 
  onClose, 
  invoice, 
  onPrintMulti, 
  onPrintSeparate,
  onSaveCallback
}) => {
  const { member, user } = useSession();
  const queryClient = useQueryClient();
  const currentUserName = member?.full_name || user?.name || 'Admin';

  const rawBrandName = invoice?.brand_name || invoice?.client || invoice?.nama_brand || '';
  // Ambil hanya nama brand murni (buang nama PIC setelah '/' atau keterangan dalam kurung)
  const brandName = rawBrandName.split('/')[0].split('(')[0].trim() || rawBrandName;
  
  // Format products list safely and sort A-Z and ml ascending
  const products = useMemo(() => {
    let list = [];
    if (invoice?.products && Array.isArray(invoice.products) && invoice.products.length > 0) {
      list = [...invoice.products];
    } else if (invoice?.product_name || invoice?.nama_product || invoice?.brand_name) {
      list = [{
        nama_product: invoice.product_name || invoice.nama_product || invoice.brand_name,
        variant_name: invoice.variant_name || invoice.product_name || '',
        qty: invoice.qty || invoice.final_qty || invoice.target_qty || 1,
        ukuran_botol_ml: invoice.ukuran_botol_ml || invoice.botol_ukuran_aktual || 0,
        ...invoice
      }];
    }

    const getBaseName = (p) => (p.nama_product || p.product_name || p.variant_name || '').replace(/(\d+)\s*ml/gi, '').trim();
    const getSizeNum = (p) => parseFloat(p.selected_size || p.ukuran_ml || p.size || p.ukuran_botol_ml || p.botol_ukuran_client) || 0;

    list.sort((a, b) => {
      const nameA = getBaseName(a);
      const nameB = getBaseName(b);
      const nameComp = nameA.localeCompare(nameB, 'id', { sensitivity: 'base' });
      if (nameComp !== 0) return nameComp;
      return getSizeNum(a) - getSizeNum(b);
    });

    return list;
  }, [invoice]);

  const [batchMasters, setBatchMasters] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [showNilai, setShowNilai] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [manualEditedIndices, setManualEditedIndices] = useState(new Set());
  
  const [savedState, setSavedState] = useState({
    isSaved: Boolean(invoice?.spk_printed_by || invoice?.spk_printed_date),
    printedBy: invoice?.spk_printed_by || '',
    printedDate: invoice?.spk_printed_date || '',
    printedAt: invoice?.spk_printed_at || ''
  });

  // Date State (YYYY-MM-DD)
  const defaultDateStr = invoice?.spk_printed_date || invoice?.naik_produksi_date || new Date().toISOString().slice(0, 10);
  const [spkDate, setSpkDate] = useState(defaultDateStr);
  
  // Batch numbers per product index: { [index]: string }
  const [batchNumberMap, setBatchNumberMap] = useState({});
  const [editingBatchIdx, setEditingBatchIdx] = useState(null);
  const [customBatchInput, setCustomBatchInput] = useState('');
  const [isRegisteringIdx, setIsRegisteringIdx] = useState(null);

  // Load Batch Masters
  const loadMasters = async () => {
    setLoadingMasters(true);
    try {
      const masters = await fetchBatchMasters();
      setBatchMasters(masters);
    } catch (e) {
      console.warn('Gagal memuat batch masters:', e);
    } finally {
      setLoadingMasters(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMasters();
      const initialDate = invoice?.spk_printed_date || invoice?.naik_produksi_date || new Date().toISOString().slice(0, 10);
      setSpkDate(initialDate);
      setManualEditedIndices(new Set());
      setSavedState({
        isSaved: Boolean(invoice?.spk_printed_by || invoice?.spk_printed_date),
        printedBy: invoice?.spk_printed_by || '',
        printedDate: invoice?.spk_printed_date || '',
        printedAt: invoice?.spk_printed_at || ''
      });
      if (products.length > 0) {
        setSelectedIndices(products.map((_, i) => i));
      }
    }
  }, [isOpen, invoice, products]);

  // Compute batch numbers when batchMasters or spkDate changes
  useEffect(() => {
    if (!isOpen || products.length === 0) return;

    const newMap = {};
    const cleanBrand = (brandName || '').toLowerCase().trim();

    let nextUnregisteredIndex = 1;
    if (batchMasters.length > 0) {
      const brandMaster = batchMasters.find(b => {
        const bName = (b.brand_name || '').trim().toLowerCase();
        const bCode = (b.brand_code || '').trim().toLowerCase();
        return bName === cleanBrand || bCode === cleanBrand;
      });
      if (brandMaster && Array.isArray(brandMaster.variants)) {
        nextUnregisteredIndex = brandMaster.variants.length + 1;
      }
    }

    const assignedUnregisteredMap = new Map();

    products.forEach((p, idx) => {
      // If manually typed in this session, keep it
      if (manualEditedIndices.has(idx) && batchNumberMap[idx]) {
        newMap[idx] = batchNumberMap[idx];
        return;
      }

      // If already has custom or saved batch number on order/product AND date matches the printed date, preserve it
      if (p.batch_number && !p.batch_number.startsWith('OMIAUM') && (!invoice?.spk_printed_date || spkDate === invoice?.spk_printed_date)) {
        newMap[idx] = p.batch_number;
        return;
      }

      const varName = p.nama_product || p.product_name || p.variant_name || '';
      const match = findBatchVariant(batchMasters, brandName, varName);

      let vIdxStr = '01';
      if (match.isFound && match.variant) {
        vIdxStr = String(match.variant.code || match.variantIndex || '01').padStart(2, '0');
      } else {
        const cleanVName = varName.toLowerCase().replace(/(\d+)\s*ml/gi, '').trim();
        if (!assignedUnregisteredMap.has(cleanVName)) {
          assignedUnregisteredMap.set(cleanVName, String(nextUnregisteredIndex++).padStart(2, '0'));
        }
        vIdxStr = assignedUnregisteredMap.get(cleanVName);
      }

      newMap[idx] = generateBatchCode({
        brandCode: match.brandCode || brandName,
        variantIndex: vIdxStr,
        dateStr: spkDate,
        monthlyCount: 1
      });
    });

    setBatchNumberMap(newMap);
  }, [isOpen, batchMasters, spkDate, products, brandName, manualEditedIndices]);

  const toggleSelectAll = () => {
    if (selectedIndices.length === products.length) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(products.map((_, i) => i));
    }
  };

  const toggleProduct = (idx) => {
    if (selectedIndices.includes(idx)) {
      setSelectedIndices(selectedIndices.filter(i => i !== idx));
    } else {
      setSelectedIndices([...selectedIndices, idx]);
    }
  };

  // Quick Register Variant to Database Master
  const handleQuickRegister = async (idx, p, match) => {
    const varName = (p.nama_product || p.product_name || p.variant_name || '').trim();
    if (!varName) {
      toast.error('Nama varian tidak valid!');
      return;
    }
    setIsRegisteringIdx(idx);
    try {
      const result = await registerNewVariantToBatchMaster(
        brandName || 'SATALA',
        varName,
        match.suggestedVariantIndex
      );

      toast.success(`Varian "${varName}" berhasil didaftarkan sebagai Varian #${result.variant.code} di Database Batch!`);
      
      // Reload masters
      await loadMasters();
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      queryClient.invalidateQueries({ queryKey: ['batch-masters'] });

      // Update generated batch code
      const updatedCode = generateBatchCode({
        brandCode: result.brandMaster.brand_code,
        variantIndex: result.variant.code,
        dateStr: spkDate,
        monthlyCount: 1
      });
      setBatchNumberMap(prev => ({ ...prev, [idx]: updatedCode }));
    } catch (err) {
      toast.error('Gagal mendaftarkan varian: ' + err.message);
    } finally {
      setIsRegisteringIdx(null);
    }
  };

  // Save manual batch edit
  const handleSaveManualBatch = (idx) => {
    if (customBatchInput.trim()) {
      const upperVal = customBatchInput.trim().toUpperCase();
      setBatchNumberMap(prev => ({
        ...prev,
        [idx]: upperVal
      }));
      setManualEditedIndices(prev => new Set([...prev, idx]));
    }
    setEditingBatchIdx(null);
    setCustomBatchInput('');
  };

  // Persist print tracking log & batch data
  const saveBatchMetadata = async (selectedProducts, isOnlySave = false) => {
    setIsSaving(true);
    const todayIso = new Date().toISOString();
    const printedBy = currentUserName;
    const printedDate = spkDate;
    const printedAt = todayIso;

    // Attach batch numbers to products
    const updatedProducts = products.map((p, idx) => ({
      ...p,
      batch_number: batchNumberMap[idx] || p.batch_number,
      spk_date: printedDate
    }));

    const updatePayload = {
      spk_printed_by: printedBy,
      spk_printed_date: printedDate,
      spk_printed_at: printedAt,
      products: updatedProducts
    };

    try {
      if (invoice?.id) {
        // 1. Coba update ke ProductionOrder
        try {
          await base44.entities.ProductionOrder.update(invoice.id, updatePayload);
        } catch (ePO) {
          // 2. Jika bukan ProductionOrder, update ke ClientPO
          try {
            await base44.entities.ClientPO.update(invoice.id, updatePayload);
          } catch (eCPO) {
            console.warn('Entity update fallback check:', eCPO);
          }
        }

        // 3. Jika ada order_number / po terkait, sinkronkan juga
        if (invoice.order_number) {
          try {
            const matchedPOs = await base44.entities.ProductionOrder.filter({ order_number: invoice.order_number });
            if (matchedPOs && matchedPOs.length > 0) {
              for (const m of matchedPOs) {
                if (m.id !== invoice.id) {
                  await base44.entities.ProductionOrder.update(m.id, updatePayload).catch(() => {});
                }
              }
            }
          } catch (err) {}
        }
      }

      // Record history logs for all selected products in one batch
      const historyEntries = selectedProducts.map(prod => {
        const finalBatchCode = prod.batch_number || batchNumberMap[0] || 'BATCH';
        return {
          batch_code: finalBatchCode,
          batch_number: finalBatchCode,
          brand_name: brandName,
          variant_name: prod.nama_product || prod.product_name || prod.variant_name || '',
          date: printedDate,
          spk_date: printedDate,
          printed_by: printedBy,
          printed_at: printedAt,
          po_number: invoice?.order_number || invoice?.id || '-'
        };
      });

      await appendBatchHistory(historyEntries);

      // Invalidate all related queries for real-time reactivity
      queryClient.invalidateQueries({ queryKey: ['batch-histories'] });
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      if (invoice?.id) {
        queryClient.invalidateQueries({ queryKey: ['production-order', invoice.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });

      // Update local state immediately
      setSavedState({
        isSaved: true,
        printedBy,
        printedDate,
        printedAt
      });

      if (typeof onSaveCallback === 'function') {
        onSaveCallback({ printedBy, printedDate, updatedProducts });
      }

      if (isOnlySave) {
        toast.success('✓ Nomor Batch berhasil disimpan & dicatat ke Riwayat Batch!');
      }

      return { success: true, updatedProducts };
    } catch (e) {
      console.error('Gagal menyimpan print tracking metadata:', e);
      toast.error('Gagal menyimpan data batch: ' + e.message);
      return { success: false };
    } finally {
      setIsSaving(false);
    }
  };

  // Hanya simpan batch tanpa mencetak dokumen PDF
  const handleOnlySaveBatch = async () => {
    const selectedProducts = selectedIndices.map(i => ({
      ...products[i],
      batch_number: batchNumberMap[i] || products[i].batch_number,
      spk_date: spkDate
    }));

    await saveBatchMetadata(selectedProducts, true);
  };

  const handlePrintMulti = async () => {
    const selectedProducts = selectedIndices.map(i => ({
      ...products[i],
      batch_number: batchNumberMap[i] || products[i].batch_number,
      spk_date: spkDate
    }));

    await saveBatchMetadata(selectedProducts, false);
    onPrintMulti(selectedProducts, showNilai, spkDate);
    onClose();
  };

  const handlePrintSeparate = async () => {
    const selectedProducts = selectedIndices.map(i => ({
      ...products[i],
      batch_number: batchNumberMap[i] || products[i].batch_number,
      spk_date: spkDate
    }));

    await saveBatchMetadata(selectedProducts, false);
    onPrintSeparate(selectedProducts, showNilai, spkDate);
    onClose();
  };

  const isAlreadySaved = savedState.isSaved || Boolean(invoice?.spk_printed_by || invoice?.spk_printed_date);
  const displayPrintedBy = savedState.printedBy || invoice?.spk_printed_by || currentUserName;
  const displayPrintedDate = savedState.printedDate || invoice?.spk_printed_date || '';
  const displayPrintedAt = savedState.printedAt || invoice?.spk_printed_at || '';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-[#FCFAF7] border-[#E6D5B8] p-0 overflow-hidden max-h-[92vh] flex flex-col rounded-3xl shadow-2xl shadow-amber-950/20">
        
        {/* Header SPK Modal (Luxury Satala Espresso Gradient) */}
        <div className="bg-gradient-to-r from-[#211A16] via-[#2F241E] to-[#1C1512] p-5 sm:p-6 text-white relative overflow-hidden shrink-0 border-b border-[#D4AF37]/20">
          
          {/* Subtle Background Glow */}
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute left-1/3 -bottom-10 w-32 h-32 bg-amber-600/10 rounded-full blur-2xl pointer-events-none" />

          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-[#D4AF37]/40 rounded-2xl flex items-center justify-center text-[#E5C378] shadow-inner">
                <Printer className="w-5 h-5 text-[#F3EAD8]" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base sm:text-lg font-black tracking-tight text-[#FAF6EE]">
                    Cetak SPK Produksi
                  </h2>
                  <span className="text-[11px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-[#3D2F27] text-[#E5C378] border border-[#D4AF37]/30 shadow-2xs">
                    {brandName || 'SPK'}
                  </span>
                </div>
                <p className="text-xs text-slate-300/80 mt-0.5 font-medium">
                  Atur tanggal SPK, verifikasi Nomor Batch, dan pilih varian yang akan dicetak.
                </p>
              </div>
            </div>
          </div>

          {/* Status Batch Banner di dalam Pop-up */}
          {isAlreadySaved ? (
            <div className="mt-4 pt-3.5 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs bg-gradient-to-r from-emerald-950/70 via-amber-950/60 to-[#2A201A]/90 p-3.5 rounded-2xl border border-emerald-500/40 backdrop-blur-sm relative z-10 shadow-inner">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-emerald-300 font-bold uppercase tracking-wider text-[10px] bg-emerald-900/80 px-2.5 py-0.5 rounded-full border border-emerald-400/30 shadow-2xs">
                      ✓ Batch Sudah Tersimpan
                    </span>
                    <span className="text-slate-300">oleh:</span>
                    <strong className="text-white font-bold tracking-wide">{displayPrintedBy}</strong>
                  </div>
                  <div className="text-amber-300/90 text-[11px] font-mono mt-0.5 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-amber-400" />
                    <span>{displayPrintedDate ? new Date(displayPrintedDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</span>
                    {displayPrintedAt && (
                      <span>• Pukul {new Date(displayPrintedAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB</span>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-[10px] text-amber-200 bg-[#302621] border border-[#D4AF37]/50 px-3 py-1 rounded-full font-bold self-start sm:self-auto shadow-2xs tracking-wide">
                ✏️ Tetap Bisa Diedit
              </span>
            </div>
          ) : (
            <div className="mt-4 pt-3.5 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs bg-amber-950/40 p-3 rounded-2xl border border-amber-500/20 relative z-10">
              <span className="flex items-center gap-2 text-slate-200 font-medium">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
                <span>Status Batch:</span>
                <strong className="text-[#E5C378] font-bold bg-amber-900/60 px-2 py-0.5 rounded-full border border-amber-400/30 text-[11px]">
                  Batch Baru (Belum Tersimpan)
                </strong>
              </span>
              <span className="text-[11px] text-slate-300 font-mono">
                {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
        </div>

        {/* Content Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-4 flex-1 custom-scrollbar">
          
          {/* Input Tanggal SPK (Luxury Warm Card) */}
          <div className="bg-white p-4 rounded-2xl border border-[#E6D5B8] shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <label className="text-xs font-bold text-[#302621] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#8C6208]" />
                  Tanggal & Bulan SPK (Acuan Batch):
                </label>
                <p className="text-[11px] text-[#7A695B] mt-0.5 leading-relaxed">
                  {isAlreadySaved 
                    ? 'Ubah tanggal ini kapan saja jika ingin memperbarui No. Batch & Exp Date dokumen SPK.' 
                    : 'Ubah tanggal untuk menyesuaikan kode hari, bulan, dan tahun pada No. Batch & Exp Date.'}
                </p>
              </div>
              <div className="w-full sm:w-52 shrink-0">
                <CustomDatePicker 
                  value={spkDate} 
                  onChange={setSpkDate} 
                  className="w-full h-9 text-xs font-bold border-[#E6D5B8] bg-slate-50/60 focus-visible:ring-[#8C6208]" 
                />
              </div>
            </div>
          </div>

          {/* List Variant & No. Batch */}
          <div>
            <div className="flex items-center justify-between mb-2.5 px-1">
              <span className="text-xs font-black text-[#302621] uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#8C6208]" />
                Varian & Nomor Batch ({products.length}):
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleSelectAll} 
                className="h-7 text-xs text-[#8C6208] hover:text-[#302621] hover:bg-amber-50 font-bold px-2.5 rounded-lg"
              >
                {selectedIndices.length === products.length ? 'Batal Pilih Semua' : 'Pilih Semua'}
              </Button>
            </div>

            <div className="space-y-2.5">
              {products.map((p, idx) => {
                const varName = p.nama_product || p.product_name || p.variant_name || 'Produk';
                const match = findBatchVariant(batchMasters, brandName, varName);
                const currentBatch = batchNumberMap[idx] || '';
                const isSelected = selectedIndices.includes(idx);
                const isEditingThis = editingBatchIdx === idx;

                return (
                  <div 
                    key={idx} 
                    className={`rounded-2xl border transition-all duration-150 ${
                      isSelected 
                        ? 'bg-gradient-to-r from-[#FAF6EE] to-white border-[#D4AF37]/60 ring-1 ring-[#D4AF37]/20 shadow-xs' 
                        : 'bg-white/70 border-slate-200 opacity-60 hover:opacity-90'
                    }`}
                  >
                    <div className="p-3.5 sm:p-4">
                      <div className="flex items-start gap-3.5">
                        <Checkbox 
                          checked={isSelected} 
                          onCheckedChange={() => toggleProduct(idx)}
                          className="mt-1 data-[state=checked]:bg-[#8C6208] data-[state=checked]:border-[#8C6208]"
                        />
                        <div className="flex-1 min-w-0">
                          {/* Nama Produk & Qty & Ukuran */}
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm font-black text-[#302621]">
                                {varName}
                              </h4>
                              {(p.selected_size || p.ukuran_ml || p.size || p.ukuran_botol_ml || p.botol_ukuran_client) && (
                                <span className="text-[11px] font-bold bg-[#F3EAD8] text-[#7A5308] border border-[#D4AF37]/40 px-2 py-0.5 rounded-lg shadow-2xs font-mono">
                                  {p.selected_size || p.ukuran_ml || p.size || p.ukuran_botol_ml || p.botol_ukuran_client} ml
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-[#7A695B] font-mono font-medium bg-slate-100/80 px-2.5 py-0.5 rounded-lg border border-slate-200/80">
                              Qty: <strong className="text-[#302621] font-bold">{p.qty || p.final_qty || p.target_qty || 1} pcs</strong>
                            </span>
                          </div>

                          {/* Database Status & No. Batch */}
                          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                            
                            {/* Left: Badge Status Database */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {match.isFound ? (
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-2xs">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                  Terdaftar #{match.variant?.code}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300 shadow-2xs">
                                  <AlertTriangle className="w-3 h-3 text-amber-700" />
                                  Belum di Master
                                </span>
                              )}

                              {!match.isFound && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isRegisteringIdx === idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickRegister(idx, p, match);
                                  }}
                                  className="h-6 text-[10px] px-2.5 bg-amber-50 hover:bg-amber-100 text-[#8C6208] border-amber-300/80 font-bold rounded-lg"
                                  title="Daftarkan varian ini ke Database Master No. Batch"
                                >
                                  {isRegisteringIdx === idx ? (
                                    <RefreshCw className="w-2.5 h-2.5 animate-spin mr-1" />
                                  ) : (
                                    <PlusCircle className="w-2.5 h-2.5 mr-1 text-[#8C6208]" />
                                  )}
                                  + Daftarkan Varian #{match.suggestedVariantIndex}
                                </Button>
                              )}
                            </div>

                            {/* Right: Display / Edit No. Batch */}
                            <div className="flex items-center gap-1.5">
                              {isEditingThis ? (
                                <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-[#D4AF37] shadow-xs">
                                  <Input
                                    size="sm"
                                    value={customBatchInput}
                                    onChange={(e) => setCustomBatchInput(e.target.value)}
                                    placeholder="No. Batch..."
                                    className="h-7 text-xs font-mono font-black w-36 uppercase border-none focus-visible:ring-0 px-2"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveManualBatch(idx);
                                      if (e.key === 'Escape') setEditingBatchIdx(null);
                                    }}
                                  />
                                  <Button 
                                    size="icon" 
                                    className="h-6 w-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                                    onClick={() => handleSaveManualBatch(idx)}
                                  >
                                    <Check className="w-3 h-3" />
                                  </Button>
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    className="h-6 w-6 text-slate-400 hover:text-slate-700 rounded-lg"
                                    onClick={() => setEditingBatchIdx(null)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5 bg-[#FAF6EE] border border-[#E6D5B8] px-3 py-1 rounded-xl shadow-2xs group">
                                  <span className="text-[10px] text-[#7A695B] font-bold uppercase tracking-wider">Batch:</span>
                                  <span className="text-xs font-mono font-black text-[#8C6208] tracking-wide">
                                    {currentBatch || '-'}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingBatchIdx(idx);
                                      setCustomBatchInput(currentBatch);
                                    }}
                                    className="p-1 text-slate-400 group-hover:text-[#8C6208] hover:bg-amber-100/60 rounded-md transition-colors ml-0.5 cursor-pointer"
                                    title="Edit No. Batch Manual"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Option Show Nilai */}
          <div className="flex items-center gap-2 pt-1 px-1 bg-white/60 p-3 rounded-xl border border-slate-200/80">
            <Checkbox 
              id="showNilai"
              checked={showNilai} 
              onCheckedChange={(checked) => setShowNilai(checked)}
              className="data-[state=checked]:bg-[#8C6208] data-[state=checked]:border-[#8C6208]"
            />
            <label htmlFor="showNilai" className="text-xs font-bold text-[#302621] cursor-pointer select-none">
              Tampilkan Kolom Nilai / Biaya di Lembar Cetak SPK
            </label>
          </div>

        </div>

        {/* Footer Actions (Frosted Glassmorphism Sticky Bar) */}
        <div className="p-4 sm:p-5 bg-white/95 backdrop-blur-md border-t border-[#E6D5B8] flex flex-col sm:flex-row items-center gap-2.5 shrink-0">
          
          {/* Tombol Simpan Batch Saja */}
          <Button 
            type="button"
            variant="outline"
            disabled={selectedIndices.length === 0 || isSaving}
            onClick={handleOnlySaveBatch}
            className="w-full sm:w-auto h-11 px-5 text-xs font-black border-[#8C6208]/70 bg-[#FBF7EE] hover:bg-[#F3EAD8] text-[#302621] flex items-center justify-center gap-2 rounded-xl shadow-xs cursor-pointer active:scale-95 transition-all"
            title="Simpan nomor batch & catat ke riwayat tanpa mencetak dokumen"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin text-[#8C6208]" />
            ) : (
              <Check className="w-4 h-4 text-[#8C6208] stroke-[3]" />
            )}
            <span>{isAlreadySaved ? 'Simpan Perubahan' : 'Simpan Batch'}</span>
          </Button>

          {/* Tombol Simpan & Cetak SPK */}
          <Button 
            onClick={handlePrintMulti}
            disabled={selectedIndices.length === 0 || isSaving}
            className="flex-1 w-full sm:w-auto bg-gradient-to-r from-[#302621] via-[#3E2E25] to-[#261E1A] hover:from-[#221A16] hover:to-[#352720] text-[#FAF6EE] font-bold h-11 text-xs shadow-md shadow-amber-950/15 flex items-center justify-center gap-2 rounded-xl border border-[#8C6208]/40 cursor-pointer"
          >
            <Printer className="w-4 h-4 text-[#E5C378]" />
            {isAlreadySaved
              ? `Simpan Perubahan & Cetak (${selectedIndices.length})`
              : `Simpan Batch & Cetak (${selectedIndices.length})`}
          </Button>

          {selectedIndices.length > 1 && (
            <Button 
              variant="outline"
              onClick={handlePrintSeparate}
              disabled={isSaving}
              className="w-full sm:w-auto text-xs font-bold h-11 border-slate-300 hover:bg-slate-50 rounded-xl px-4 cursor-pointer text-slate-700"
            >
              Cetak Terpisah ({selectedIndices.length})
            </Button>
          )}

          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full sm:w-auto text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 h-11 px-4 rounded-xl cursor-pointer"
          >
            Batal
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default SpkPrintModal;
